import { superpathjoin } from 'superpathjoin';
import { localeSelector } from '@gandi/translate';
import { logging } from 'gandi.redux-logger';
import { fetchCurrentSharing, fetchCurrentSharingAndSetPersonal, fetchPersonal, setCurrentId } from '../modules/organizations/actions';
import { selectCurrent, selectCurrentId, selectPersonal, selectPersonalId } from '../modules/organizations/selectors';
export const serverRouterConfig = (endpoint, hasPublicRoutes = false, hasSubsetOrgSupport = false, persistSharingId = true) => (store, req, res) => {
  const logger = logging(store.dispatch, '@gandi/redux-sharing/router');
  const redirectWithCookieSharing = (locale, cookieSharingId, originalPathWithoutBasename) => {
    if (cookieSharingId) {
      const redirectUrl = superpathjoin(endpoint, locale, cookieSharingId, originalPathWithoutBasename, true);
      logger.info(`No sharing id in url but in cookie sharing_id. Redirecting to ${redirectUrl}`);
      res.redirect(302, redirectUrl);
      return Promise.reject(new Error('No sharing id in url but in cookie sharing_id'));
    }
    return Promise.reject(new Error('Internal error: missing cookieSharingId'));
  };
  const uuidRE = new RegExp(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i);
  let originalPathWithoutBasename = req.url.replace(endpoint, '').replace(/^\//, '');
  const state = store.getState();
  const locale = hasPublicRoutes ? localeSelector(state) : '';
  if (hasPublicRoutes) {
    if (!locale) {
      return Promise.reject(new Error('Missing locale'));
    }
    const urlLocale = originalPathWithoutBasename.split('/')[0].split(/[?#]/)[0];
    if (!urlLocale || urlLocale !== locale) {
      const redirectUrl = superpathjoin(endpoint, locale, originalPathWithoutBasename, true);
      logger.info(`No locale in url. Redirecting to ${redirectUrl}`);
      res.header('X-Cache-Control-After-Gzip', 'max-age=600, s-maxage=3600, stale-while-revalidate=10800, stale-if-error=10800, public, no-transform');
      res.append('Vary', 'Accept-Language');
      res.redirect(302, redirectUrl);
      return Promise.reject(new Error('No locale in url'));
    }
    originalPathWithoutBasename = originalPathWithoutBasename.replace(urlLocale, '').replace(/^\//, '');
    if (!req.tokeninfo) {
      const sharingId = originalPathWithoutBasename.split('/')[0].split(/[?#]/)[0];
      originalPathWithoutBasename = originalPathWithoutBasename.replace(sharingId, '').replace(/^\//, '');
      if (sharingId.match(uuidRE)) {
        const redirectUrl = superpathjoin(endpoint, locale, originalPathWithoutBasename, true);
        logger.info('Not authenticated. Found extraneous sharing id in url. ' + `Redirecting to ${redirectUrl}`);
        res.redirect(302, redirectUrl);
        return Promise.reject(new Error('Found extraneous sharing id in url'));
      }
      const basename = superpathjoin(endpoint, locale, true);
      return Promise.resolve({
        basename
      });
    }
  }
  const sharingId = originalPathWithoutBasename.split('/')[0].split(/[?#]/)[0];
  logger.info(`Extract sharingId \`${sharingId}\` from url \`${req.url}\``);
  if (!sharingId) {
    if (persistSharingId) {
      const cookieSharingId = req.cookies.get('sharing_id');
      if (cookieSharingId) {
        return redirectWithCookieSharing(locale, cookieSharingId, originalPathWithoutBasename);
      }
    }
    if (req.tokeninfo) {
      const redirectUrl = superpathjoin(endpoint, locale, req.tokeninfo.user_id, true);
      logger.info(`No sharing id either url or cookie. Redirecting to ${redirectUrl}`);
      res.redirect(302, redirectUrl);
    }
    return Promise.reject(new Error('No sharing id in url'));
  }
  if (!sharingId.match(uuidRE)) {
    if (persistSharingId) {
      const cookieSharingId = req.cookies.get('sharing_id');
      if (cookieSharingId) {
        return redirectWithCookieSharing(locale, cookieSharingId, originalPathWithoutBasename);
      }
    }
    const redirectUrl = superpathjoin(endpoint, locale, req.tokeninfo.user_id, originalPathWithoutBasename, true);
    logger.info(`No sharing id in url. Redirecting to ${redirectUrl}`);
    res.redirect(302, redirectUrl);
    return Promise.reject(new Error('No sharing id in url'));
  }
  originalPathWithoutBasename = originalPathWithoutBasename.replace(sharingId, '').replace(/^\//, '');
  return store.dispatch(fetchCurrentSharingAndSetPersonal(sharingId, req, hasSubsetOrgSupport)).then(() => {
    if (persistSharingId) {
      res.cookie('sharing_id', sharingId, {
        httpOnly: true,
        secure: true,
        maxAge: 1 * 365 * 24 * 60 * 60 * 1000,
        path: endpoint
      });
    }
    const basename = superpathjoin(endpoint, locale, sharingId, true);
    return {
      basename
    };
  }).catch(error => {
    const updatedState = store.getState();
    const personalId = selectPersonalId(updatedState);
    const isTimeout = error.originalError?.type === 'request-timeout';
    if (isTimeout || !personalId || sharingId === personalId) {
      const currentId = selectCurrentId(updatedState);
      if (!currentId) {
        logger.warn('Warning: unexpected case happened. As a last resort, ' + `try setting the sharing_id ${sharingId} as the current id, ` + "as it's the user's personal id. It means the user exists in oauth " + 'but not in the organizations database. But we will let the app ' + 'handle the error management instead of ending with infinite redirects.');
        store.dispatch(setCurrentId(sharingId));
      }
      const basename = superpathjoin(endpoint, locale, sharingId, true);
      return {
        basename
      };
    }
    const redirectUrl = superpathjoin(endpoint, locale, req.tokeninfo.user_id, originalPathWithoutBasename, true);
    res.redirect(302, redirectUrl);
    const sharingIdError = error;
    sharingIdError.message = `Invalid sharing id: ${error.message}`;
    if (!!error.statusCode && error.statusCode !== 403) {
      logger.error({
        err: sharingIdError
      }, `Invalid sharing id: ${error.message}`);
    }
    return Promise.reject(sharingIdError);
  });
};
export const clientRouterConfig = (endpoint, hasPublicRoutes = false, pivoineEndpoint = '/api/v5') => async store => {
  if (typeof window !== 'undefined') {
    const {
      pathname
    } = window.location;
    if (pathname.lastIndexOf(pivoineEndpoint, 0) === 0) {
      return Promise.reject(new Error('Error page'));
    }
  }
  const state = store.getState();
  const locale = hasPublicRoutes ? localeSelector(state) : '';
  const currentId = selectCurrentId(state);
  if (hasPublicRoutes) {
    if (!locale) {
      return Promise.reject(new Error('Missing locale'));
    }
  } else if (!currentId) {
    return Promise.reject(new Error('Missing sharing id'));
  }
  const basename = superpathjoin(endpoint, locale, currentId, true);
  const routerOptions = {
    basename
  };
  const currentSharing = selectCurrent(state);
  const personalOrga = selectPersonal(state);
  const personalId = selectPersonalId(state);
  const promises = [];
  if (currentId && !currentSharing) {
    promises.push(store.dispatch(fetchCurrentSharing(currentId, false)));
  }
  if (personalId && !personalOrga && currentId !== personalId) {
    promises.push(store.dispatch(fetchPersonal()));
  }
  await Promise.allSettled(promises);
  return Promise.resolve(routerOptions);
};
const routerConfigs = {
  server: serverRouterConfig,
  client: clientRouterConfig
};
export default routerConfigs;